import type { KaiToolActivity } from "./kaiTypes";
import { isGraphMailDocumentFilename } from "@/lib/graphMailDocuments";
import {
  normalizeSourceAttachments,
  type KaiSourceAttachment,
} from "@/features/rates/create/document/aiRateDraft";

/** A downloadable source-file attachment surfaced by a Kai tool result. */
export interface FileRef {
  batchId: string;
  attachmentId: string;
  label: string;
  toolName: string;
  filename?: string;
}

/**
 * A parsed bill-of-lading document surfaced by the
 * `api_get_booking_bl_documents` tool, with its Graph attachment reference for
 * download. Rendered as a compact card in the Kai tool group.
 */
export interface BlDocumentSummary {
  /** Stable key for rendering (attachment id, else bl number + index). */
  key: string;
  documentType: string;
  blNumber?: string | null;
  filename?: string | null;
  portOfLoading?: string | null;
  portOfDischarge?: string | null;
  vessel?: string | null;
  containerCount: number;
  receivedAt?: string | null;
  /** Graph attachment coordinates for `downloadGraphAttachment`. */
  mailboxId?: string | null;
  messageId?: string | null;
  attachmentId?: string | null;
  downloadable: boolean;
}

/**
 * A downloadable Graph-mail (Outlook) document attachment surfaced by a mail
 * tool's top-level `graph_attachments` block (`get_graph_mail_message`,
 * `get_graph_mail_context`, `get_graph_mail_thread`, `list_booking_graph_mail`,
 * `get_graph_mail_attachment`). Rendered as Download / Preview chips in the Kai
 * tool group — the same download coordinates the booking BL card uses.
 */
export interface GraphMailAttachmentSummary {
  /** Stable key for rendering/dedup (message id + attachment id). */
  key: string;
  mailboxId: string;
  messageId: string;
  attachmentId: string;
  filename: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  messageSubject?: string | null;
  category?: string | null;
  receivedAt?: string | null;
  downloadable: boolean;
}

/** Tools that return a top-level `graph_attachments` array for Download / Preview. */
const GRAPH_MAIL_ATTACHMENT_TOOLS = new Set([
  "get_graph_mail_message",
  "get_graph_mail_context",
  "get_graph_mail_thread",
  "list_booking_graph_mail",
  "get_graph_mail_attachment",
  "api_get_booking_bl_documents",
  "api_get_booking_documents",
  "api_get_booking_ledger",
]);

/** A report-generation descriptor surfaced by a Kai tool result. */
export interface ReportDownloadRef {
  /** Stable key for dedup/rendering. */
  key: string;
  reportType: string;
  label: string;
  date?: string;
  config?: Record<string, unknown>;
}

/**
 * A downloadable rate-card source document that lives on a Kai conversation
 * upload rather than an ingested batch file. Kai-created rate cards store their
 * original document (PDF / XLSX / WhatsApp image) under
 * `provenance.source_attachments` (on `api_list_rate_cards` / `api_get_rate_card`)
 * or `kai_source_attachments` (on `get_batch_summary`). Rendered as a Download
 * card so the chat can surface the file even when `batch_attachments` is empty.
 */
export interface KaiRateSourceAttachmentSummary {
  /** Stable key for dedup/rendering (`conversationId:attachmentId`). */
  key: string;
  conversationId: string;
  attachmentId: string;
  filename: string;
  /** Optional rate-card context for the label subtitle. */
  carrier?: string | null;
  contractRef?: string | null;
}

/** Tools whose rate-card rows carry `provenance.source_attachments`. */
const RATE_CARD_PROVENANCE_TOOLS = new Set([
  "api_list_rate_cards",
  "api_get_rate_card",
]);

/**
 * Extract Kai-upload rate-card source documents from a tool result. Reads
 * `provenance.source_attachments` on rate-card list/detail tools and
 * `kai_source_attachments` on `get_batch_summary`; returns `[]` for other tools
 * or malformed JSON.
 */
export function extractRateSourceAttachments(
  toolName: string,
  resultStr: string,
): KaiRateSourceAttachmentSummary[] {
  if (
    !RATE_CARD_PROVENANCE_TOOLS.has(toolName) &&
    toolName !== "get_batch_summary"
  ) {
    return [];
  }

  try {
    const parsed = JSON.parse(resultStr);
    const out: KaiRateSourceAttachmentSummary[] = [];
    const seen = new Set<string>();

    const pushFrom = (
      rawAttachments: unknown,
      ctx: { carrier?: unknown; contractRef?: unknown },
    ) => {
      const normalized = normalizeSourceAttachments(
        Array.isArray(rawAttachments)
          ? (rawAttachments as KaiSourceAttachment[])
          : undefined,
      );
      for (const a of normalized) {
        const key = `${a.conversationId}:${a.attachmentId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          key,
          conversationId: a.conversationId,
          attachmentId: a.attachmentId,
          filename: a.filename,
          carrier: typeof ctx.carrier === "string" ? ctx.carrier : null,
          contractRef: typeof ctx.contractRef === "string" ? ctx.contractRef : null,
        });
      }
    };

    if (RATE_CARD_PROVENANCE_TOOLS.has(toolName)) {
      const cards = Array.isArray(parsed?.data)
        ? parsed.data
        : parsed && typeof parsed === "object"
          ? [parsed]
          : [];
      for (const card of cards) {
        if (!card || typeof card !== "object") continue;
        const obj = card as Record<string, unknown>;
        const prov = obj.provenance;
        const sourceAttachments =
          prov && typeof prov === "object"
            ? (prov as Record<string, unknown>).source_attachments
            : undefined;
        pushFrom(sourceAttachments, {
          carrier: obj.carrier,
          contractRef: obj.contract_ref,
        });
      }
      return out;
    }

    // get_batch_summary
    const refs = parsed?.kai_source_attachments;
    if (Array.isArray(refs)) {
      for (const ref of refs) {
        if (!ref || typeof ref !== "object") continue;
        const obj = ref as Record<string, unknown>;
        pushFrom([obj as KaiSourceAttachment], {
          carrier: obj.carrier,
          contractRef: obj.contract_ref,
        });
      }
    }
    return out;
  } catch {
    return [];
  }
}

export type FileCategory = "rate_card" | "booking_update" | "batch";

export function getFileCategory(toolName: string): FileCategory | null {
  if (toolName.includes("rate_card") || toolName.includes("rate_line")) {
    return "rate_card";
  }
  if (
    toolName.includes("booking_update") ||
    toolName === "api_get_update_history"
  ) {
    return "booking_update";
  }
  if (toolName === "get_batch_summary") return "batch";
  return null;
}

function buildFileLabel(
  category: FileCategory,
  obj: Record<string, unknown>,
): string {
  if (category === "rate_card") {
    const parts = ["Rate Card"];
    if (obj.carrier) parts.push(String(obj.carrier));
    if (obj.contract_ref) parts.push(String(obj.contract_ref));
    return parts.join(" \u2014 ");
  }
  if (category === "booking_update") {
    const parts = ["Booking Update"];
    if (obj.source) parts.push(String(obj.source));
    return parts.join(" \u2014 ");
  }
  return "Source File";
}

/**
 * Extract downloadable source-file references (batch + attachment ids) from a
 * tool result string. Recognises rate-card, booking-update, and batch-summary
 * tools; returns `[]` for everything else or on malformed JSON.
 */
export function extractFileRefs(toolName: string, resultStr: string): FileRef[] {
  const category = getFileCategory(toolName);
  if (!category) return [];

  try {
    const parsed = JSON.parse(resultStr);
    const refs: FileRef[] = [];
    const seen = new Set<string>();

    const addRef = (obj: Record<string, unknown>) => {
      const batchId = obj.batch_id;
      const attachmentId = obj.batch_attachment_id;
      if (typeof batchId !== "string" || typeof attachmentId !== "string") {
        return;
      }
      if (seen.has(attachmentId)) return;
      seen.add(attachmentId);
      refs.push({
        batchId,
        attachmentId,
        label: buildFileLabel(category, obj),
        toolName,
      });
    };

    if (toolName === "get_batch_summary") {
      const batch = parsed?.batch;
      const attachments = parsed?.attachments;
      if (Array.isArray(attachments) && batch?.id) {
        for (const att of attachments) {
          if (att?.id && !seen.has(att.id)) {
            seen.add(att.id);
            refs.push({
              batchId: batch.id,
              attachmentId: att.id,
              label: att.filename || "Attachment",
              toolName,
              filename: att.filename,
            });
          }
        }
      }
      return refs;
    }

    if (Array.isArray(parsed?.data)) {
      for (const item of parsed.data) addRef(item);
    } else if (Array.isArray(parsed)) {
      for (const item of parsed) addRef(item);
    } else if (typeof parsed === "object" && parsed !== null) {
      addRef(parsed);
      if (Array.isArray(parsed.lines)) {
        for (const line of parsed.lines) addRef(line);
      }
    }

    return refs;
  } catch {
    return [];
  }
}

/**
 * Extract report-download descriptors from a tool result. Currently only
 * `api_generate_booking_report` / `api_generate_schedule_report` return a
 * `download` descriptor (the file is generated on click via `generate-preview`).
 */
export function extractReportDownloadRefs(
  toolName: string,
  resultStr: string,
): ReportDownloadRef[] {
  if (
    toolName !== "api_generate_booking_report" &&
    toolName !== "api_generate_schedule_report"
  ) {
    return [];
  }

  const defaultReportType =
    toolName === "api_generate_schedule_report"
      ? "schedule_dump"
      : "booking_dump";

  try {
    const parsed = JSON.parse(resultStr);
    if (!parsed || typeof parsed !== "object") return [];
    const download = parsed.download;
    if (!download || typeof download !== "object") return [];

    const reportType =
      typeof parsed.report_type === "string"
        ? parsed.report_type
        : defaultReportType;
    const config =
      parsed.config && typeof parsed.config === "object"
        ? (parsed.config as Record<string, unknown>)
        : undefined;
    const date = typeof parsed.date === "string" ? parsed.date : undefined;
    const label =
      typeof download.label === "string" && download.label.trim()
        ? download.label
        : "Download report";
    const key = `${reportType}:${date ?? ""}:${JSON.stringify(config ?? {})}`;

    return [{ key, reportType, label, date, config }];
  } catch {
    return [];
  }
}

/**
 * Extract parsed bill-of-lading documents from an `api_get_booking_bl_documents`
 * tool result. Reads the `documents` array shaped by the MCP handler; returns
 * `[]` for other tools or malformed JSON.
 */
export function extractBlDocuments(
  toolName: string,
  resultStr: string,
): BlDocumentSummary[] {
  if (toolName !== "api_get_booking_bl_documents") return [];

  try {
    const parsed = JSON.parse(resultStr);
    const documents = parsed?.documents;
    if (!Array.isArray(documents)) return [];

    const summaries: BlDocumentSummary[] = [];
    documents.forEach((doc: Record<string, unknown>, idx: number) => {
      if (!doc || typeof doc !== "object") return;
      const asStr = (v: unknown): string | null =>
        typeof v === "string" && v.trim() !== "" ? v : null;
      const attachmentId = asStr(doc.attachment_id);
      const blNumber = asStr(doc.bl_number);
      const containers = Array.isArray(doc.containers) ? doc.containers.length : 0;
      summaries.push({
        key: attachmentId ?? `${blNumber ?? "bl"}-${idx}`,
        documentType:
          typeof doc.document_type === "string" ? doc.document_type : "bl",
        blNumber,
        filename: asStr(doc.filename),
        portOfLoading: asStr(doc.port_of_loading),
        portOfDischarge: asStr(doc.port_of_discharge),
        vessel: asStr(doc.vessel),
        containerCount: containers,
        receivedAt: asStr(doc.received_at),
        mailboxId: asStr(doc.mailbox_id),
        messageId: asStr(doc.message_id),
        attachmentId,
        downloadable: doc.downloadable === true,
      });
    });
    return summaries;
  } catch {
    return [];
  }
}

/**
 * Extract downloadable Graph-mail document attachments from a mail tool's
 * top-level `graph_attachments` array. Non-document attachments (inline images,
 * signatures) are filtered out via {@link isGraphMailDocumentFilename}; returns
 * `[]` for other tools or malformed JSON.
 */
export function extractGraphMailAttachments(
  toolName: string,
  resultStr: string,
): GraphMailAttachmentSummary[] {
  if (!GRAPH_MAIL_ATTACHMENT_TOOLS.has(toolName)) return [];

  try {
    const parsed = JSON.parse(resultStr);
    const refs = parsed?.graph_attachments;
    if (!Array.isArray(refs)) return [];

    const asStr = (v: unknown): string | null =>
      typeof v === "string" && v.trim() !== "" ? v : null;

    const summaries: GraphMailAttachmentSummary[] = [];
    for (const ref of refs) {
      if (!ref || typeof ref !== "object") continue;
      const mailboxId = asStr(ref.mailbox_id);
      const messageId = asStr(ref.message_id);
      const attachmentId = asStr(ref.attachment_id);
      const filename = asStr(ref.filename);
      if (!mailboxId || !messageId || !attachmentId || !filename) continue;
      // Only PDFs / Excel — never surface signature images as downloads.
      if (!isGraphMailDocumentFilename(filename)) continue;
      summaries.push({
        key: `${messageId}:${attachmentId}`,
        mailboxId,
        messageId,
        attachmentId,
        filename,
        contentType: asStr(ref.content_type),
        sizeBytes:
          typeof ref.size_bytes === "number" ? ref.size_bytes : null,
        messageSubject: asStr(ref.message_subject),
        category: asStr(ref.category),
        receivedAt: asStr(ref.received_at),
        downloadable: ref.downloadable === true,
      });
    }
    return summaries;
  } catch {
    return [];
  }
}

/** Aggregate all download references across a tool group. */
export function collectToolDownloads(tools: KaiToolActivity[]): {
  fileRefs: FileRef[];
  reportRefs: ReportDownloadRef[];
  blDocuments: BlDocumentSummary[];
  graphMailAttachments: GraphMailAttachmentSummary[];
  rateSourceAttachments: KaiRateSourceAttachmentSummary[];
} {
  const fileRefs: FileRef[] = [];
  const reportRefs: ReportDownloadRef[] = [];
  const blDocuments: BlDocumentSummary[] = [];
  const graphMailAttachments: GraphMailAttachmentSummary[] = [];
  const rateSourceAttachments: KaiRateSourceAttachmentSummary[] = [];
  const seenFiles = new Set<string>();
  const seenReports = new Set<string>();
  const seenBl = new Set<string>();
  const seenGraphMail = new Set<string>();
  const seenRateSource = new Set<string>();

  for (const tool of tools) {
    if (!tool.result) continue;
    for (const ref of extractFileRefs(tool.name, tool.result)) {
      if (seenFiles.has(ref.attachmentId)) continue;
      seenFiles.add(ref.attachmentId);
      fileRefs.push(ref);
    }
    for (const ref of extractReportDownloadRefs(tool.name, tool.result)) {
      if (seenReports.has(ref.key)) continue;
      seenReports.add(ref.key);
      reportRefs.push(ref);
    }
    for (const doc of extractBlDocuments(tool.name, tool.result)) {
      if (seenBl.has(doc.key)) continue;
      seenBl.add(doc.key);
      blDocuments.push(doc);
    }
    for (const att of extractGraphMailAttachments(tool.name, tool.result)) {
      if (seenGraphMail.has(att.key)) continue;
      seenGraphMail.add(att.key);
      graphMailAttachments.push(att);
    }
    for (const att of extractRateSourceAttachments(tool.name, tool.result)) {
      if (seenRateSource.has(att.key)) continue;
      seenRateSource.add(att.key);
      rateSourceAttachments.push(att);
    }
  }

  return {
    fileRefs,
    reportRefs,
    blDocuments,
    graphMailAttachments,
    rateSourceAttachments,
  };
}
